import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  MaintenanceRecord,
  MaintenanceType,
  PaginatedResponse,
  CreateMaintenancePayload,
} from '@/lib/types';

export interface MaintenanceFilters {
  page?: number;
  page_size?: number;
  type?: MaintenanceType | '';
  asset?: number | '';
  date_after?: string;
  date_before?: string;
  ordering?: string;
}

export interface MaintenanceRecordWithDetails extends MaintenanceRecord {
  asset_name?: string;
  asset_serial_number?: string;
}

export function useMaintenance(filters: MaintenanceFilters = {}) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );

  return useQuery({
    queryKey: queryKeys.maintenance.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<MaintenanceRecordWithDetails>> => {
      const response = await apiClient.get<PaginatedResponse<MaintenanceRecordWithDetails>>(
        '/api/v1/maintenance/',
        { params: cleanFilters }
      );
      return response.data;
    },
  });
}

export function useCreateMaintenance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMaintenancePayload): Promise<MaintenanceRecord> => {
      const response = await apiClient.post<MaintenanceRecord>('/api/v1/maintenance/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useUpdateMaintenance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateMaintenancePayload>;
    }): Promise<MaintenanceRecord> => {
      const response = await apiClient.patch<MaintenanceRecord>(
        `/api/v1/maintenance/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}

export function useDeleteMaintenance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/maintenance/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.maintenance.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assets.all });
    },
  });
}
